import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Bold, Italic, Link2, List, ListOrdered, Undo2, Redo2 } from 'lucide-react';

/**
 * Editor rich-text liviano basado en Tiptap.
 * Props:
 *  - value (HTML string, controlled-lite: se aplica cuando cambia por prop, no en cada keystroke)
 *  - onChange(html)
 *  - placeholder
 *  - autoFocus
 */
export function RichTextEditor({ value, onChange, placeholder = 'Escribí tu respuesta…', autoFocus = false, minHeight = 140, editable = true }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3, 4] },
        link: false,
        bulletList: { HTMLAttributes: { class: 'list-disc pl-6 my-2' } },
        orderedList: { HTMLAttributes: { class: 'list-decimal pl-6 my-2' } },
      }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank', class: 'text-purple-700 underline' } }),
    ],
    content: value || '',
    editable,
    autofocus: autoFocus && editable ? 'end' : false,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        'data-testid': 'rich-text-editor',
        class: 'prose prose-sm max-w-none focus:outline-none px-3 py-2',
        style: `min-height: ${minHeight}px;`,
      },
    },
  });

  // Reflejar cambios dinámicos de `editable`
  useEffect(() => {
    if (editor && editor.isEditable !== editable) {
      editor.setEditable(editable);
    }
  }, [editable, editor]);

  // Sincronizar `value` externo (ej: al insertar canned response o firma)
  useEffect(() => {
    if (!editor) return;
    // Solo actualizar si el value externo difiere del actual, para no romper el cursor.
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes('link').href;
    const url = window.prompt('URL del enlace:', prev || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const btnCls = (active) =>
    `p-1.5 rounded hover:bg-purple-100 ${active ? 'bg-purple-100 text-purple-700' : 'text-gray-600'}`;

  return (
    <div className="border rounded-lg bg-white overflow-hidden" data-testid="rich-editor-wrapper">
      <div className="flex items-center gap-0.5 border-b bg-gray-50 px-1 py-1">
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btnCls(editor.isActive('bold'))} title="Negrita" data-testid="editor-bold">
          <Bold className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btnCls(editor.isActive('italic'))} title="Cursiva" data-testid="editor-italic">
          <Italic className="w-4 h-4" />
        </button>
        <button type="button" onClick={setLink} className={btnCls(editor.isActive('link'))} title="Enlace" data-testid="editor-link">
          <Link2 className="w-4 h-4" />
        </button>
        <div className="w-px h-5 bg-gray-300 mx-1"></div>
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btnCls(editor.isActive('bulletList'))} title="Lista" data-testid="editor-bullet">
          <List className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btnCls(editor.isActive('orderedList'))} title="Lista numerada" data-testid="editor-numbered">
          <ListOrdered className="w-4 h-4" />
        </button>
        <div className="flex-1" />
        <button type="button" onClick={() => editor.chain().focus().undo().run()} className={btnCls(false)} title="Deshacer" data-testid="editor-undo">
          <Undo2 className="w-4 h-4" />
        </button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()} className={btnCls(false)} title="Rehacer" data-testid="editor-redo">
          <Redo2 className="w-4 h-4" />
        </button>
      </div>
      <div style={{ maxHeight: Math.max(minHeight + 40, 240), overflow: 'auto' }}>
        <EditorContent editor={editor} />
      </div>
      {(!value || value === '<p></p>' || value === '') && (
        <div className="absolute pointer-events-none text-gray-400 text-sm px-4 py-2 -mt-40 hidden">{placeholder}</div>
      )}
    </div>
  );
}

export default RichTextEditor;
